/**
 * PublicShopBrowser — Unified browse page for all public shops (no auth required).
 * Route: /browse
 *
 * Merges the old PublicShopBrowser (search, filters, categories) with
 * ShopSelector's card design (full-bleed image, blur overlay, preview animation).
 *
 * Data sources:
 *   - /api/shops/public  → shop info (name, address, category, sections)
 *   - /api/datasets       → 3D scan data (camera count, paths for preview animation)
 *   Joined on Dataset.shopId === Shop.id
 *
 * Features:
 *   - Search by name / address
 *   - Category filter chips
 *   - Full-bleed image cards with glassmorphism info overlay
 *   - Preview animation on hover/scroll (cross-fade frame sequence)
 *   - Camera count badge
 *   - SPA navigation to 3D viewer via setCurrentShop()
 *   - "View on Map" toggle → /map
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useShopStore, getEnrichedShops, type EnrichedPublicShop } from '../../stores/shopStore'
import { useAuthStore } from '../../stores/authStore'
import { useAppStore } from '@store/appStore'
import { useShopCardPreview } from '@hooks/useShopCardPreview'
import type { ShopMetadata } from '@app-types/shopMetadata'
import { apiUrl, authFetch, posterUrl, resolveUrl, toBlobUrl } from '../../config'
import { buildMorphUrl, buildMorphUrlFromBase } from '@/utils/buildMorphUrl'
import ViewerChoiceModal from './ViewerChoiceModal'
import { ShareModal } from '../ShareModal/ShareModal'
import Mall3DViewer from './Mall3D/Mall3DViewer'
import ScanPanel from './ScanPanel'
export { CATEGORY_STYLES, DEFAULT_CAT_STYLE, getCatStyle } from './categoryStyles'
import { getCatStyle } from './categoryStyles'
import './PublicShopBrowser.css'

const CATEGORIES = ['Grocery', 'Electronics', 'Clothing', 'Restaurant', 'Pharmacy', 'Supermarket', 'Cafe', 'Bakery']

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

interface Props {
  embedded?: boolean
  initialSearch?: string
}

export default function PublicShopBrowser({ embedded = false, initialSearch = '' }: Props) {
  const navigate = useNavigate()
  const { t } = useTranslation('shops')
  const { publicShops, publicShopsLoading, publicDatasetsLoading, publicDatasets, fetchPublicShops, fetchPublicDatasets } = useShopStore()
  const setAvailableDatasets = useAppStore((state) => state.setAvailableDatasets)
  const setAvailableShops = useAppStore((state) => state.setAvailableShops)
  const setCurrentShop = useAppStore((state) => state.setCurrentShop)
  const [search, setSearch] = useState(initialSearch)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [pendingShop, setPendingShop] = useState<EnrichedPublicShop | null>(null)
  const [viewMode, setViewMode] = useState<'carousel' | 'grid' | 'reels' | 'space'>('carousel')
  const [mallOpen, setMallOpen] = useState(false)
  const [carouselHintVisible, setCarouselHintVisible] = useState(true)
  const [filterOpen, setFilterOpen] = useState(false)
  const [sharedShopId, setSharedShopId] = useState<string | null>(null)
  const [shareModal, setShareModal] = useState<{ url: string; shopName: string; shopId: string } | null>(null)

  // Filter sheet state
  const [filterNearest, setFilterNearest] = useState(false)
  const [filterMinRating, setFilterMinRating] = useState<number | null>(null)
  const [filterMinReviews, setFilterMinReviews] = useState<number | null>(null)
  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(null)
  const [locationLoading, setLocationLoading] = useState(false)
  const [locationError, setLocationError] = useState<string | null>(null)

  // Sync search when parent updates initialSearch (e.g. home search bar)
  useEffect(() => { setSearch(initialSearch) }, [initialSearch])

  // Track searches (debounced — fires 1s after user stops typing)
  useEffect(() => {
    const q = search.trim()
    if (!q || q.length < 2) return
    const timer = setTimeout(() => {
      import('../../services/activityService').then(({ trackSearch }) => {
        trackSearch(q)
      })
    }, 1000)
    return () => clearTimeout(timer)
  }, [search])

  // Shop metadata for preview animation
  const [shopMetadata, setShopMetadata] = useState<Map<string, ShopMetadata>>(new Map())
  const metadataLoadedRef = useRef<Set<string>>(new Set())

  // Preview animation hook
  const { previewFrame, previousFrame, previewEffects, isPreviewing, currentShopRef, startPreview, stopPreview } = useShopCardPreview()

  // Hover timeout refs for delayed stop
  const hoverTimeoutRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const shopRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const currentCenterShopRef = useRef<string | null>(null)
  const carouselRef = useRef<HTMLDivElement>(null)

  // Fetch both data sources on mount
  useEffect(() => {
    fetchPublicShops()
    fetchPublicDatasets().then(() => {
      // Also populate appStore so useShopCardPreview can find datasets
      const { publicDatasets } = useShopStore.getState()
      setAvailableDatasets(publicDatasets)
      const shopIds = publicDatasets.map(d => d.id)
      setAvailableShops(shopIds)
    })
  }, [])

  // Get enriched shops (joined data)
  const enrichedShops = useMemo(() => getEnrichedShops(), [publicShops, publicDatasets])

  // Client-side filter for instant feedback
  const filtered = useMemo(() => {
    let list = enrichedShops
    if (selectedCategory) {
      list = list.filter((s) => s.category?.toLowerCase() === selectedCategory.toLowerCase())
    }
    if (filterMinRating !== null) {
      list = list.filter((s) => ((s as any).ratingsSummary?.average ?? 0) >= filterMinRating!)
    }
    if (filterMinReviews !== null) {
      list = list.filter((s) => ((s as any).ratingsSummary?.count ?? 0) >= filterMinReviews!)
    }
    if (filterNearest && userCoords) {
      list = [...list].sort((a, b) => {
        const dist = (s: EnrichedPublicShop) => {
          const loc = s.location
          if (!loc?.lat || !loc?.lng) return Infinity
          return haversineKm(userCoords.lat, userCoords.lng, loc.lat, loc.lng)
        }
        return dist(a) - dist(b)
      })
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      list = list.filter(
        (s) => s.name.toLowerCase().includes(q) || (s.address && s.address.toLowerCase().includes(q))
      )
    }
    return list
  }, [enrichedShops, search, selectedCategory, filterMinRating, filterMinReviews, filterNearest, userCoords])

  const shopsWithLocation = filterNearest && userCoords
    ? filtered.filter(s => s.location?.lat && s.location?.lng).length
    : 0

  const activeFilterCount =
    (filterNearest ? 1 : 0) +
    (filterMinRating !== null ? 1 : 0) +
    (filterMinReviews !== null ? 1 : 0) +
    (selectedCategory !== null ? 1 : 0)

  const clearAllFilters = () => {
    setFilterNearest(false)
    setFilterMinRating(null)
    setFilterMinReviews(null)
    setSelectedCategory(null)
    setLocationError(null)
  }

  const handleNearestToggle = () => {
    const next = !filterNearest
    setFilterNearest(next)
    setLocationError(null)
    if (next && !userCoords) {
      if (!navigator.geolocation) {
        setLocationError('Geolocation is not supported by your browser')
        setFilterNearest(false)
        return
      }
      setLocationLoading(true)
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude })
          setLocationLoading(false)
        },
        (err) => {
          setLocationLoading(false)
          setFilterNearest(false)
          if (err.code === 1) setLocationError('Location permission denied. Please allow location access in your browser.')
          else if (err.code === 2) setLocationError('Location unavailable. Please try again.')
          else setLocationError('Could not get your location. Please try again.')
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
      )
    }
  }

  // Extract unique categories from actual data for chips
  const availableCategories = useMemo(() => {
    const cats = new Set(publicShops.map((s) => s.category).filter(Boolean))
    return CATEGORIES.filter((c) => cats.has(c)).concat(
      [...cats].filter((c) => !CATEGORIES.includes(c))
    )
  }, [publicShops])

  // Load shop metadata lazily for visible cards (for preview animation)
  const loadMetadataForShop = useCallback(async (shop: EnrichedPublicShop) => {
    if (!shop.dataset || metadataLoadedRef.current.has(shop.id)) return
    metadataLoadedRef.current.add(shop.id)
    try {
      const metadataPath = shop.dataset.paths?.metadata || `/api/scan-data/${shop.dataset.id}/shop_metadata.json`
      const response = await fetch(toBlobUrl(resolveUrl(metadataPath)))
      if (response.ok) {
        const metadata: ShopMetadata = await response.json()
        setShopMetadata(prev => new Map(prev).set(shop.dataset!.id, metadata))
      }
    } catch (error) {
      console.warn(`[PublicShopBrowser] Failed to load metadata for ${shop.id}:`, error)
    }
  }, [])

  // IntersectionObserver to lazy-load metadata for visible cards
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const shopId = entry.target.getAttribute('data-shop-id')
            if (shopId) {
              const shop = enrichedShops.find(s => s.id === shopId)
              if (shop) loadMetadataForShop(shop)
            }
          }
        }
      },
      { rootMargin: '200px' }
    )
    shopRefs.current.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [enrichedShops, loadMetadataForShop])

  // Scroll-based auto-play: start preview for card closest to viewport center
  useEffect(() => {
    if (shopRefs.current.size === 0) return

    const findCardInCenter = (): string | null => {
      const viewportCenter = window.innerHeight / 2
      let closestId: string | null = null
      let closestDist = Infinity
      shopRefs.current.forEach((el, id) => {
        const rect = el.getBoundingClientRect()
        const dist = Math.abs(rect.top + rect.height / 2 - viewportCenter)
        if (dist < closestDist) { closestDist = dist; closestId = id }
      })
      return closestId
    }

    const handleScroll = () => {
      const centerShop = findCardInCenter()
      if (centerShop === currentCenterShopRef.current) return
      currentCenterShopRef.current = centerShop
      if (centerShop) {
        const shop = enrichedShops.find(s => s.id === centerShop)
        const datasetId = shop?.dataset?.id
        if (datasetId && shopMetadata.has(datasetId)) {
          startPreview(datasetId, shopMetadata)
        }
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [enrichedShops, shopMetadata, startPreview])

  // 3D coverflow effect for carousel mode
  useEffect(() => {
    if (viewMode !== 'carousel') return
    const el = carouselRef.current
    if (!el) return

    const apply3D = () => {
      const cW = el.clientWidth
      const center = el.scrollLeft + cW / 2
      const items = el.querySelectorAll<HTMLElement>('.shop-card--carousel')
      items.forEach((item) => {
        const itemCenter = item.offsetLeft + item.offsetWidth / 2
        const dist = (itemCenter - center) / item.offsetWidth
        const clampedDist = Math.max(-2.5, Math.min(2.5, dist))
        const ry = clampedDist * -28
        const scale = 1 - Math.min(Math.abs(clampedDist) * 0.1, 0.22)
        const tz = -Math.abs(clampedDist) * 90
        const op = Math.max(0.4, 1 - Math.abs(clampedDist) * 0.32)
        item.style.transform = `perspective(1100px) rotateY(${ry}deg) scale(${scale}) translateZ(${tz}px)`
        item.style.opacity = String(op)
        if (Math.abs(dist) < 0.5) {
          item.classList.add('is-center')
        } else {
          item.classList.remove('is-center')
        }
      })
    }

    const onFirstScroll = () => {
      setCarouselHintVisible(false)
      el.removeEventListener('scroll', onFirstScroll)
    }

    requestAnimationFrame(apply3D)
    el.addEventListener('scroll', apply3D, { passive: true })
    el.addEventListener('scroll', onFirstScroll, { passive: true, once: true })
    window.addEventListener('resize', apply3D, { passive: true })
    return () => {
      el.removeEventListener('scroll', apply3D)
      window.removeEventListener('resize', apply3D)
    }
  }, [viewMode, filtered])

  // Mouse click-drag horizontal scroll for carousel (desktop mice that lack
  // touch/trackpad gestures). Touch scrolling is untouched — this only binds
  // mouse events.
  useEffect(() => {
    if (viewMode !== 'carousel') return
    const el = carouselRef.current
    if (!el) return

    let dragging = false
    let startX = 0
    let startScroll = 0
    let moved = false

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return
      dragging = true
      moved = false
      startX = e.clientX
      startScroll = el.scrollLeft
    }
    const onMouseMove = (e: MouseEvent) => {
      if (!dragging) return
      const dx = e.clientX - startX
      if (Math.abs(dx) > 4) moved = true
      el.scrollLeft = startScroll - dx
    }
    const stop = () => {
      if (!dragging) return
      dragging = false
      el.classList.remove('is-mouse-dragging')
    }
    const onMouseMoveCursor = (e: MouseEvent) => {
      if (dragging && moved) el.classList.add('is-mouse-dragging')
      onMouseMove(e)
    }
    const onClickCapture = (e: MouseEvent) => {
      if (moved) { e.stopPropagation(); e.preventDefault() }
    }

    el.addEventListener('mousedown', onMouseDown)
    window.addEventListener('mousemove', onMouseMoveCursor)
    window.addEventListener('mouseup', stop)
    el.addEventListener('click', onClickCapture, true)
    return () => {
      el.removeEventListener('mousedown', onMouseDown)
      window.removeEventListener('mousemove', onMouseMoveCursor)
      window.removeEventListener('mouseup', stop)
      el.removeEventListener('click', onClickCapture, true)
    }
  }, [viewMode, filtered])

  const handleShopClick = useCallback((shop: EnrichedPublicShop) => {
    if (shop.has3DViewer && shop.dataset) {
      // Auto-select original viewer — skip choice modal
      setCurrentShop(shop.dataset.id)
      window.location.href = `/?shopid=${shop.dataset.id}`
    } else if (shop.has3DViewer) {
      // Scanner-uploaded shop — open morph viewer in-place (no page reload)
      const firstSection = (shop.sections ?? []).find((s: any) => s.camerasJsonUrl)
      if (firstSection?.camerasJsonUrl) {
        const base = firstSection.camerasJsonUrl.replace(/\/cameras\.json$/, '/')
        buildMorphUrlFromBase(base).then((url) => {
          navigate(`/?morphUrl=${encodeURIComponent(url)}`)
        }).catch(() => navigate(`/shop/${shop.id}`))
      } else {
        navigate(`/shop/${shop.id}`)
      }
    } else {
      navigate(`/shop/${shop.id}`)
    }
  }, [navigate])

  const handleOriginalViewer = useCallback(() => {
    if (!pendingShop?.dataset) return
    setCurrentShop(pendingShop.dataset.id)
    window.location.href = `/?shopid=${pendingShop.dataset.id}`
  }, [pendingShop, setCurrentShop])

  const handleMorphViewer = useCallback(async () => {
    if (!pendingShop?.dataset) return
    const url = await buildMorphUrl(pendingShop.dataset)
    window.location.href = url
  }, [pendingShop])

  const isLoading = publicShopsLoading || publicDatasetsLoading

  // Build morph viewer share URL for a shop
  const buildShareUrl = useCallback(async (shop: EnrichedPublicShop): Promise<string> => {
    if (!shop.dataset) return window.location.origin
    const paths = shop.dataset.paths
    const camerasPath = paths.cameras || `/api/scan-data/${shop.dataset.id}/section_0/cameras.json`
    const apiPrefix = '/api/scan-data/'
    const blobRelative = camerasPath.startsWith(apiPrefix)
      ? camerasPath.substring(apiPrefix.length, camerasPath.lastIndexOf('/') + 1)
      : camerasPath.substring(0, camerasPath.lastIndexOf('/') + 1)
    try {
      const res = await authFetch(apiUrl('/api/scan-data-token'))
      if (res.ok) {
        const { base_url, sas_token } = await res.json()
        const base = `${base_url}/${blobRelative}`
        const params = new URLSearchParams({ base, sas: sas_token })
        return `${window.location.origin}/morph-viewer/morph-viewer-v2.html?${params.toString()}`
      }
    } catch { /* fall through */ }
    const base = apiUrl(`${apiPrefix}${blobRelative}`)
    return `${window.location.origin}/morph-viewer/morph-viewer-v2.html?base=${encodeURIComponent(base)}`
  }, [])

  const handleShareShop = useCallback(async (e: React.MouseEvent, shop: EnrichedPublicShop) => {
    e.stopPropagation()
    const url = `https://dockaan.com/?shopid=${encodeURIComponent(shop.id)}`
    setShareModal({ url, shopName: shop.name || 'Shop', shopId: shop.id })
  }, [])

  return (
    <div className={`shop-browser${embedded ? ' shop-browser--embedded' : ''}${viewMode === 'space' ? ' shop-browser--discover' : ''}`}>
      {/* Header — hidden in embedded mode */}
      <header className="shop-browser-header">
        <button className="shop-browser-home" onClick={() => navigate('/')}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>
          Home
        </button>
        <h1>{t('browseShops')}</h1>
        <button className="shop-browser-map-btn" onClick={() => navigate('/map')}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M15 3l-6 2.25L3 3v18l6-2.25L15 21l6-2.25V3L15 3zm0 15.4l-6-2.25V5.6l6 2.25v10.55z"/></svg>
          Map
        </button>
      </header>

      {/* Search */}
      <div className="shop-browser-search-bar">
        <input
          type="text"
          placeholder={t('searchShopsPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="shop-browser-search"
        />
      </div>

      {/* ── Toolbar: mode toggle + filter ── */}
      <div className="shop-browser-toolbar">
        {/* Explore / 3D Mall toggle pill (Discover/space mode entry commented out — see below) */}
        <div className="space-mode-toggle">
          <button
            type="button"
            className="space-mode-toggle-option active"
            onClick={() => setViewMode('carousel')}
            title="Explore"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
              <rect x="1" y="5" width="5" height="14" rx="2" opacity="0.55"/>
              <rect x="9" y="3" width="6" height="18" rx="2.5"/>
              <rect x="18" y="5" width="5" height="14" rx="2" opacity="0.55"/>
            </svg>
            Explore
          </button>
          {/* Was the "Discover" option (entered space viewMode). Replaced with 3D Mall entry point.
          <span className={`space-mode-toggle-option${viewMode === 'space' ? ' active' : ''}`}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="12" r="3"/><circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2"/>
              <circle cx="19" cy="5" r="2"/><circle cx="5" cy="18" r="1.5"/>
            </svg>
            Discover
          </span>
          */}
          <button
            type="button"
            className="space-mode-toggle-option"
            onClick={() => setMallOpen(true)}
            title="Walk the 3D Mall"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2 3 7v10l9 5 9-5V7l-9-5zm0 2.3L18.5 8 12 11.7 5.5 8 12 4.3zM5 9.7l6 3.4v7.2l-6-3.3V9.7zm8 10.6v-7.2l6-3.4v7.3l-6 3.3z"/>
            </svg>
            3D Mall
          </button>
          <span className="space-mode-toggle-pill" />
        </div>

        <button
          className={`shop-browser-filter-btn${filterOpen ? ' active' : ''}${activeFilterCount > 0 ? ' has-filter' : ''}`}
          onClick={() => setFilterOpen(true)}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 4a1 1 0 0 1 1-1h16a1 1 0 0 1 .8 1.6l-6 8v5.4a1 1 0 0 1-.55.9l-4 2A1 1 0 0 1 9 20v-7.4L3.2 4.6A1 1 0 0 1 3 4z"/>
          </svg>
          Filters
          {activeFilterCount > 0 && <span className="shop-browser-filter-badge">{activeFilterCount}</span>}
        </button>
      </div>

      {/* Nearest filter status bar */}
      {filterNearest && userCoords && (
        <div style={{ padding: '6px 16px', fontSize: 12, color: shopsWithLocation > 0 ? '#4ade80' : '#f87171', background: 'rgba(0,0,0,0.3)' }}>
          {shopsWithLocation > 0
            ? `📍 Sorted by distance — ${shopsWithLocation} store${shopsWithLocation !== 1 ? 's' : ''} with location`
            : '📍 No stores have location data yet'}
        </div>
      )}
      {filterNearest && locationLoading && (
        <div style={{ padding: '6px 16px', fontSize: 12, color: '#94a3b8', background: 'rgba(0,0,0,0.3)' }}>
          ⏳ Getting your location...
        </div>
      )}

      {/* Filter bottom-sheet modal */}
      {filterOpen && (
        <div className="filter-sheet-overlay" onClick={() => setFilterOpen(false)}>
          <div className="filter-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="filter-sheet-handle" />
            <div className="filter-sheet-header">
              <span className="filter-sheet-title">Filter Stores</span>
              {activeFilterCount > 0 && (
                <button className="filter-sheet-clear" onClick={clearAllFilters}>Clear all</button>
              )}
            </div>

            {/* Sort by */}
            <p className="filter-sheet-section-label">SORT BY</p>
            <button
              className={`filter-toggle-row${filterNearest ? ' active' : ''}`}
              onClick={handleNearestToggle}
              disabled={locationLoading}
            >
              <span className="filter-toggle-emoji">{locationLoading ? '⏳' : '📍'}</span>
              <div className="filter-toggle-text">
                <span className="filter-toggle-label">Nearest to me</span>
                <span className="filter-toggle-sub">
                  {locationLoading ? 'Getting your location...' : 'Sort stores by distance'}
                </span>
              </div>
              <div className={`filter-toggle-switch${filterNearest ? ' on' : ''}`}>
                <div className={`filter-toggle-thumb${filterNearest ? ' on' : ''}`} />
              </div>
            </button>
            {locationError && (
              <p style={{ color: '#f87171', fontSize: 12, margin: '4px 0 8px', padding: '0 4px' }}>
                ⚠️ {locationError}
              </p>
            )}

            {/* Category */}
            {availableCategories.length > 0 && (
              <>
                <p className="filter-sheet-section-label">CATEGORY</p>
                <div className="filter-chips-row">
                  {availableCategories.map((cat) => (
                    <button
                      key={cat}
                      className={`filter-chip${selectedCategory === cat ? ' active' : ''}`}
                      onClick={() => setSelectedCategory(selectedCategory === cat ? null : cat)}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* Minimum rating */}
            <p className="filter-sheet-section-label">MINIMUM RATING</p>
            <div className="filter-chips-row">
              {[4, 3, 2].map((r) => (
                <button
                  key={r}
                  className={`filter-chip${filterMinRating === r ? ' active' : ''}`}
                  onClick={() => setFilterMinRating(filterMinRating === r ? null : r)}
                >
                  {'★'.repeat(r)}&nbsp;{r}+
                </button>
              ))}
            </div>

            {/* Minimum reviews */}
            <p className="filter-sheet-section-label">MINIMUM REVIEWS</p>
            <div className="filter-chips-row">
              {[10, 50, 100].map((n) => (
                <button
                  key={n}
                  className={`filter-chip${filterMinReviews === n ? ' active' : ''}`}
                  onClick={() => setFilterMinReviews(filterMinReviews === n ? null : n)}
                >
                  {n}+ reviews
                </button>
              ))}
            </div>

            <button
              className="filter-apply-btn"
              style={{ opacity: activeFilterCount > 0 ? 1 : 0.55 }}
              onClick={() => setFilterOpen(false)}
            >
              Show {filtered.length} store{filtered.length !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      )}

      {/* Results */}
      <div className="shop-browser-body">
        {isLoading ? (
          <div className="shop-browser-loading">
            <div className="shop-browser-spinner" />
            {t('loadingShops')}
          </div>
        ) : filtered.length === 0 ? (
          <div className="shop-browser-empty">
            {publicShops.length === 0 ? t('noPublicShopsYet') : t('noShopsMatchSearch')}
          </div>
        ) : viewMode === 'space' ? (
          <SpaceSystem
            shops={filtered}
            onShopClick={handleShopClick}
            onShare={handleShareShop}
            onExploreMode={() => setViewMode('carousel')}
            onFilterOpen={() => setFilterOpen(true)}
            activeFilterCount={activeFilterCount}
          />
        ) : viewMode === 'carousel' ? (
          <div className="shop-browser-carousel-wrap">
            {carouselHintVisible && (
              <div className={`carousel-hint${carouselHintVisible ? '' : ' carousel-hint--hidden'}`}>
                <div className="carousel-hint-arrows">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                </div>
                <span className="carousel-hint-label">Swipe to explore</span>
              </div>
            )}
            <div className="shop-browser-carousel" ref={carouselRef}>
            {filtered.map((shop) => (
              <ShopCard
                key={shop.id}
                shop={shop}
                shopRefs={shopRefs}
                isPreviewing={isPreviewing}
                previewFrame={previewFrame}
                previousFrame={previousFrame}
                previewEffects={previewEffects}
                currentPreviewShop={currentShopRef.current}
                shopMetadata={shopMetadata}
                hoverTimeoutRef={hoverTimeoutRef}
                startPreview={startPreview}
                stopPreview={stopPreview}
                onClick={() => handleShopClick(shop)}
                carouselMode
                onShare={(e) => handleShareShop(e, shop)}
                isShared={sharedShopId === shop.id}
                distanceKm={filterNearest && userCoords && shop.location?.lat ? haversineKm(userCoords.lat, userCoords.lng, shop.location.lat, shop.location.lng) : null}
              />
            ))}
            </div>
          </div>
        ) : viewMode === 'reels' ? (
          <div className="shop-browser-reels">
            {filtered.map((shop) => (
              <ShopCard
                key={shop.id}
                shop={shop}
                shopRefs={shopRefs}
                isPreviewing={isPreviewing}
                previewFrame={previewFrame}
                previousFrame={previousFrame}
                previewEffects={previewEffects}
                currentPreviewShop={currentShopRef.current}
                shopMetadata={shopMetadata}
                hoverTimeoutRef={hoverTimeoutRef}
                startPreview={startPreview}
                stopPreview={stopPreview}
                onClick={() => handleShopClick(shop)}
                reelsMode
                onShare={(e) => handleShareShop(e, shop)}
                isShared={sharedShopId === shop.id}
                distanceKm={filterNearest && userCoords && shop.location?.lat ? haversineKm(userCoords.lat, userCoords.lng, shop.location.lat, shop.location.lng) : null}
              />
            ))}
          </div>
        ) : (
          <div className="shop-browser-grid">
            {filtered.map((shop) => (
              <ShopCard
                key={shop.id}
                shop={shop}
                shopRefs={shopRefs}
                isPreviewing={isPreviewing}
                previewFrame={previewFrame}
                previousFrame={previousFrame}
                previewEffects={previewEffects}
                currentPreviewShop={currentShopRef.current}
                shopMetadata={shopMetadata}
                hoverTimeoutRef={hoverTimeoutRef}
                startPreview={startPreview}
                stopPreview={stopPreview}
                onClick={() => handleShopClick(shop)}
                onShare={(e) => handleShareShop(e, shop)}
                isShared={sharedShopId === shop.id}
                distanceKm={filterNearest && userCoords && shop.location?.lat ? haversineKm(userCoords.lat, userCoords.lng, shop.location.lat, shop.location.lng) : null}
              />
            ))}
          </div>
        )}
      </div>
      {pendingShop && (
        <ViewerChoiceModal
          shopName={pendingShop.name}
          onOriginal={handleOriginalViewer}
          onMorph={handleMorphViewer}
          onClose={() => setPendingShop(null)}
        />
      )}
      {shareModal && (
        <ShareModal
          url={shareModal.url}
          shopName={shareModal.shopName}
          shopId={shareModal.shopId}
          onClose={() => setShareModal(null)}
        />
      )}
      {mallOpen && (
        <Mall3DViewer
          shops={filtered}
          onExit={() => setMallOpen(false)}
          onShopClick={handleShopClick}
          onShare={handleShareShop}
        />
      )}
    </div>
  )
}

/* ── ShopCard: Full-bleed image with glassmorphism info overlay ── */

interface ShopCardProps {
  shop: EnrichedPublicShop
  shopRefs: React.MutableRefObject<Map<string, HTMLDivElement>>
  isPreviewing: boolean
  previewFrame: string | null
  previousFrame: string | null
  previewEffects: { currentOpacity?: number; previousOpacity?: number }
  currentPreviewShop: string | null
  shopMetadata: Map<string, ShopMetadata>
  hoverTimeoutRef: React.MutableRefObject<Map<string, ReturnType<typeof setTimeout>>>
  startPreview: (shopId: string, metadata: Map<string, ShopMetadata>) => void
  stopPreview: () => void
  onClick: () => void
  reelsMode?: boolean
  carouselMode?: boolean
  onShare?: (e: React.MouseEvent) => void
  isShared?: boolean
  distanceKm?: number | null
}

function ShopCard({
  shop, shopRefs, isPreviewing, previewFrame, previousFrame,
  previewEffects, currentPreviewShop, shopMetadata, hoverTimeoutRef,
  startPreview, stopPreview, onClick, reelsMode = false, carouselMode = false, onShare, isShared = false, distanceKm,
}: ShopCardProps) {
  const { t } = useTranslation('shops')
  const posterSrc = posterUrl(shop.id)
  const [imgError, setImgError] = useState(false)
  const datasetId = shop.dataset?.id

  // Follow state
  const user = useAuthStore((s) => s.user)
  const token = useAuthStore((s) => s.token)
  const [following, setFollowing] = useState(false)
  const [followLoading, setFollowLoading] = useState(false)
  useEffect(() => {
    if (!user || !token) return
    fetch(apiUrl(`/api/shops/${shop.id}/following`), { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : { following: false })
      .then((d) => setFollowing(d.following))
      .catch(() => {})
  }, [shop.id, user, token])
  async function handleFollow(e: React.MouseEvent) {
    e.stopPropagation()
    if (!user || !token) { window.location.href = '/login'; return }
    setFollowLoading(true)
    try {
      if (following) {
        await fetch(apiUrl(`/api/shops/${shop.id}/follow`), { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } })
        setFollowing(false)
      } else {
        await fetch(apiUrl(`/api/shops/${shop.id}/follow`), { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } })
        setFollowing(true)
      }
    } catch { /* ignore */ } finally { setFollowLoading(false) }
  }

  // 3-dot menu state
  const [menuOpen, setMenuOpen] = useState(false)
  const navigate = useNavigate()

  // Thumbnail error fallback chain: poster → thumbnail.jpg → frame_0000.jpg → placeholder
  const handleThumbnailError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget
    const currentSrc = img.src
    if (currentSrc.includes('/poster') && shop.dataset) {
      const basePath = shop.dataset.paths?.metadata?.replace('/shop_metadata.json', '') || `/api/scan-data/${shop.dataset.id}`
      img.src = toBlobUrl(resolveUrl(`${basePath}/thumbnail.jpg?shop=${shop.dataset.id}`))
    } else if (currentSrc.includes('thumbnail.jpg') && shop.dataset) {
      const basePath = shop.dataset.paths?.metadata?.replace('/shop_metadata.json', '') || `/api/scan-data/${shop.dataset.id}`
      img.src = toBlobUrl(resolveUrl(`${basePath}/section_0/frames/frame_0000.jpg`))
    } else {
      setImgError(true)
    }
  }

  const isThisCardPreviewing = isPreviewing && datasetId && currentPreviewShop === datasetId

  return (
    <div
      ref={(el) => {
        if (el) shopRefs.current.set(shop.id, el)
      }}
      className={`shop-card${reelsMode ? ' shop-card--reel' : ''}${carouselMode ? ' shop-card--carousel' : ''}`}
      data-shop-id={shop.id}
      onClick={onClick}
      onMouseEnter={() => {
        if (!datasetId) return
        const existingTimeout = hoverTimeoutRef.current.get(datasetId)
        if (existingTimeout) clearTimeout(existingTimeout)
        if (shopMetadata.has(datasetId)) {
          startPreview(datasetId, shopMetadata)
        }
      }}
      onMouseLeave={() => {
        if (!datasetId) return
        const timeout = setTimeout(() => {
          stopPreview()
          hoverTimeoutRef.current.delete(datasetId)
        }, 500)
        hoverTimeoutRef.current.set(datasetId, timeout)
      }}
      role="button"
      tabIndex={0}
      aria-label={`${shop.name}${shop.cameraCount ? ` — ${shop.cameraCount} cameras` : ''}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() }
      }}
    >
      {/* Full-bleed background image */}
      <div className="shop-card-thumbnail-container">
        {!imgError ? (
          <img
            className="shop-card-img"
            src={posterSrc}
            alt={`${shop.name} thumbnail`}
            loading="lazy"
            onError={handleThumbnailError}
          />
        ) : (
          <div className="shop-card-img-placeholder">🏪</div>
        )}

        {/* Preview animation overlay (cross-fade between frames) */}
        {isThisCardPreviewing && (
          <>
            {previousFrame && (
              <img
                className="shop-card-preview-frame"
                src={previousFrame}
                alt=""
                style={{ opacity: previewEffects.previousOpacity ?? 0, zIndex: 10 }}
              />
            )}
            {previewFrame && (
              <img
                className="shop-card-preview-frame"
                src={previewFrame}
                alt=""
                style={{ opacity: previewEffects.currentOpacity ?? 1, zIndex: 11 }}
              />
            )}
          </>
        )}

        {/* Gradient overlay for text readability */}
        <div className="shop-card-overlay" />

        {/* "Coming soon" indicator for shops without 3D data */}
        {!shop.has3DViewer && (
          <div className="shop-card-coming-soon">{t('comingSoon')}</div>
        )}

        {/* 3-dot menu — stays in thumbnail for non-carousel modes */}
        {!carouselMode && (
          <div className="shop-card-menu-wrap" onClick={(e) => e.stopPropagation()}>
            <button
              className="shop-card-menu-btn"
              onClick={(e) => { e.stopPropagation(); setMenuOpen(true) }}
              title="More options"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/>
              </svg>
            </button>
            {menuOpen && createPortal(
              <div className="shop-card-modal-overlay" onClick={() => setMenuOpen(false)}>
                <div className="shop-card-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="shop-card-modal-header">
                    <span className="shop-card-modal-title">{shop.name}</span>
                    <button className="shop-card-modal-close" onClick={() => setMenuOpen(false)}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                  {onShare && shop.has3DViewer && (
                    <button className="shop-card-menu-item" onClick={(e) => { setMenuOpen(false); onShare(e) }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                      </svg>
                      Share
                    </button>
                  )}
                  <button
                    className={`shop-card-menu-item${following ? ' shop-card-menu-item--active' : ''}`}
                    onClick={async (e) => { setMenuOpen(false); await handleFollow(e) }}
                    disabled={followLoading}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                      <line x1="12" y1="2" x2="12" y2="4"/>
                    </svg>
                    {following ? 'Unfollow' : 'Follow'}
                  </button>
                  <button
                    className="shop-card-menu-item"
                    onClick={() => { setMenuOpen(false); navigate(`/reviews/${shop.id}`) }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                    </svg>
                    Reviews
                  </button>
                  <button
                    className="shop-card-menu-item shop-card-menu-item--danger"
                    onClick={() => { setMenuOpen(false); window.location.href = `/report?shopId=${shop.id}` }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/>
                    </svg>
                    Report scan
                  </button>
                </div>
              </div>,
              document.body
            )}
          </div>
        )}
      </div>

      {/* Info panel */}
      <div className="shop-card-info">
        {/* In carousel: name row with 3-dot menu on the right */}
        <div className="shop-card-name-row">
          <div className="shop-card-name">{shop.name}</div>
          {carouselMode && (
            <div className="shop-card-menu-wrap shop-card-menu-wrap--inline" onClick={(e) => e.stopPropagation()}>
              <button
                className="shop-card-menu-btn shop-card-menu-btn--flat"
                onClick={(e) => { e.stopPropagation(); setMenuOpen(true) }}
                title="More options"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/>
                </svg>
              </button>
              {menuOpen && createPortal(
                <div className="shop-card-modal-overlay" onClick={() => setMenuOpen(false)}>
                  <div className="shop-card-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="shop-card-modal-header">
                      <span className="shop-card-modal-title">{shop.name}</span>
                      <button className="shop-card-modal-close" onClick={() => setMenuOpen(false)}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </button>
                    </div>
                    {onShare && shop.has3DViewer && (
                      <button className="shop-card-menu-item" onClick={(e) => { setMenuOpen(false); onShare(e) }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                        </svg>
                        Share
                      </button>
                    )}
                    <button
                      className={`shop-card-menu-item${following ? ' shop-card-menu-item--active' : ''}`}
                      onClick={async (e) => { setMenuOpen(false); await handleFollow(e) }}
                      disabled={followLoading}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                        <line x1="12" y1="2" x2="12" y2="4"/>
                      </svg>
                      {following ? 'Unfollow' : 'Follow'}
                    </button>
                    <button
                      className="shop-card-menu-item"
                      onClick={() => { setMenuOpen(false); navigate(`/reviews/${shop.id}`) }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                      </svg>
                      Reviews
                    </button>
                    <button
                      className="shop-card-menu-item shop-card-menu-item--danger"
                      onClick={() => { setMenuOpen(false); window.location.href = `/report?shopId=${shop.id}` }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/>
                      </svg>
                      Report scan
                    </button>
                  </div>
                </div>,
                document.body
              )}
            </div>
          )}
        </div>
        <div className="shop-card-meta">
          {shop.category && <span className="shop-card-category">{shop.category}</span>}
          <span className="shop-card-sections">
            {t('sectionCount', { count: shop.sections?.length || 0 })}
          </span>
          {distanceKm != null && (
            <span className="shop-card-distance">
              📍 {distanceKm < 1 ? `${Math.round(distanceKm * 1000)}m` : `${distanceKm.toFixed(1)}km`}
            </span>
          )}
        </div>
        {carouselMode && (
          <button
            className="shop-card-cta"
            onClick={(e) => { e.stopPropagation(); onClick() }}
          >
            {shop.has3DViewer ? 'Enter Experience' : 'View Shop'}
          </button>
        )}
      </div>
    </div>
  )
}

/* ════════════════════════════════════════════════════════════════
   SPACE EXPLORER — infinite-canvas free-roam discovery mode
════════════════════════════════════════════════════════════════ */

const UNIVERSE_W = 3600
const UNIVERSE_H = 2800
const MINIMAP_W  = 164
const MINIMAP_H  = Math.round(MINIMAP_W * (UNIVERSE_H / UNIVERSE_W))
const MM_SCALE   = MINIMAP_W / UNIVERSE_W
const SUN_X      = UNIVERSE_W / 2   // 1800
const SUN_Y      = UNIVERSE_H / 2   // 1400
const SUN_RADIUS = 72

const PLANET_SIZES = [80, 65, 92, 70, 76, 58, 86, 72, 62, 78, 60, 88, 68, 74, 82]


// Deterministic stars in universe-space
const STARS = Array.from({ length: 140 }, (_, i) => ({
  x: Math.round(((Math.sin(i * 13.7 + 1) * 0.5 + 0.5)) * UNIVERSE_W),
  y: Math.round(((Math.cos(i * 9.3  + 2) * 0.5 + 0.5)) * UNIVERSE_H),
  r: 0.7 + (i % 4) * 0.55,
  op: 0.18 + (i % 7) * 0.08,
  tw: (i * 0.41) % 6,
}))

interface PlanetPos { shopIndex: number; x: number; y: number; size: number }
interface ClusterData {
  category: string; cx: number; cy: number; nebulaR: number; ringR: number
  color: string; rgb: string; planets: PlanetPos[]
}

// Group shops by category into clusters orbiting the sun; scatter uncategorized
function computeClusterLayout(shops: EnrichedPublicShop[]) {
  const byCategory = new Map<string, number[]>()
  const uncategorized: number[] = []

  shops.forEach((shop, i) => {
    const cat = shop.category?.trim()
    if (cat) {
      if (!byCategory.has(cat)) byCategory.set(cat, [])
      byCategory.get(cat)!.push(i)
    } else {
      uncategorized.push(i)
    }
  })

  const categories = Array.from(byCategory.entries())
  const n = categories.length

  // Golden angle gives the most even spread with no two clusters in the same direction
  const GOLDEN_ANGLE = 2.39996 // radians ≈ 137.5°

  const clusters: ClusterData[] = categories.map(([category, indices], ci) => {
    const style = getCatStyle(category)

    // Spread clusters organically: golden angle + varied radius so they fill the universe
    const angle = ci * GOLDEN_ANGLE + 0.4
    // Deterministic radius variation: closer ones interleaved with far ones
    const baseR  = 340
    const radiusBand = ci % 4  // 0→near, 1→mid, 2→far, 3→very-far
    const orbitR = baseR + radiusBand * 180 + Math.abs(Math.sin(ci * 3.1)) * 100

    const cx = SUN_X + Math.cos(angle) * Math.min(orbitR, UNIVERSE_W * 0.38)
    const cy = SUN_Y + Math.sin(angle) * Math.min(orbitR, UNIVERSE_H * 0.38)

    const count = indices.length
    const innerR = count <= 1 ? 0 : Math.min(50 + count * 18, 160)
    // ringR must fully enclose all planets: innerR + max jitter (14) + max planet half-size (46) + padding
    const ringR = count <= 1 ? 72 : innerR + 80
    const nebulaR = ringR + 35

    const planets: PlanetPos[] = indices.map((shopIdx, pi) => {
      let px: number, py: number
      if (count === 1) {
        px = cx; py = cy
      } else if (count <= 6) {
        const pAngle = (pi / count) * Math.PI * 2 - Math.PI / 2
        const jitter = Math.sin(shopIdx * 7.3) * 14
        px = Math.round(cx + Math.cos(pAngle) * (innerR + jitter))
        py = Math.round(cy + Math.sin(pAngle) * (innerR + jitter))
      } else {
        // Inner ring: first 5, outer ring: rest
        const isOuter = pi >= 5
        const ri = isOuter ? pi - 5 : pi
        const rc = isOuter ? count - 5 : 5
        const r  = isOuter ? innerR : innerR * 0.55
        const pAngle = (ri / rc) * Math.PI * 2 - Math.PI / 2
        px = Math.round(cx + Math.cos(pAngle) * r)
        py = Math.round(cy + Math.sin(pAngle) * r)
      }
      return { shopIndex: shopIdx, x: px, y: py, size: PLANET_SIZES[shopIdx % PLANET_SIZES.length] }
    })

    return { category, cx: Math.round(cx), cy: Math.round(cy), nebulaR, ringR: Math.round(ringR), color: style.color, rgb: style.rgb, planets }
  })

  // Uncategorized: placed in outer space — minimum radius 1300 so they never overlap cluster rings
  // (max cluster orbit ~980 + max ringR ~240 = ~1220, so 1300 is a safe clear boundary)
  const freePlanets: PlanetPos[] = uncategorized.map((shopIdx, fi) => {
    const angle = fi * GOLDEN_ANGLE + 1.1 + Math.sin(shopIdx * 4.3) * 0.3
    const r = 1300 + (fi % 4) * 90 + Math.abs(Math.sin(shopIdx * 2.9)) * 80
    return {
      shopIndex: shopIdx,
      x: Math.round(SUN_X + Math.cos(angle) * Math.min(r, UNIVERSE_W * 0.44)),
      y: Math.round(SUN_Y + Math.sin(angle) * Math.min(r, UNIVERSE_H * 0.44)),
      size: PLANET_SIZES[shopIdx % PLANET_SIZES.length],
    }
  })

  return { clusters, freePlanets }
}

interface SpaceSystemProps {
  shops: EnrichedPublicShop[]
  onShopClick: (s: EnrichedPublicShop) => void
  onShare: (e: React.MouseEvent, s: EnrichedPublicShop) => void
  onExploreMode: () => void
  onFilterOpen: () => void
  activeFilterCount: number
}

const MIN_SCALE = 0.22
const MAX_SCALE = 2.8

function SpaceSystem({ shops, onShopClick, onShare, onExploreMode, onFilterOpen, activeFilterCount }: SpaceSystemProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  // All drag + pinch state in one mutable ref to avoid re-render mid-gesture
  const dragRef = useRef({
    active: false, sx: 0, sy: 0, ox: 0, oy: 0,
    vx: 0, vy: 0, lx: 0, ly: 0, lt: 0, moved: false,
    pinching: false, pinchDist0: 0, pinchScale0: 1,
    pinchMx: 0, pinchMy: 0,  // pinch midpoint in container coords
  })
  const rafRef    = useRef<number | null>(null)
  const scaleRef  = useRef(1.0)   // mirrors scale state, readable in closures
  const [scale, setScaleState] = useState(1.0)
  const [offset, setOffset]    = useState({ x: 0, y: 0 })
  const [viewSize, setViewSize] = useState({ w: 800, h: 520 })
  const [selectedShop, setSelectedShop] = useState<EnrichedPublicShop | null>(null)
  const [hintVisible, setHintVisible]   = useState(true)
  const [minimapW, setMinimapW] = useState(() => window.innerWidth <= 599 ? 110 : MINIMAP_W)

  const setScale = (s: number) => { scaleRef.current = s; setScaleState(s) }

  const layout = useMemo(() => computeClusterLayout(shops), [shops])

  // Centre universe on mount / resize
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const obs = new ResizeObserver(([e]) => {
      const { width, height } = e.contentRect
      setViewSize({ w: width, h: height })
      setOffset(prev => {
        if (prev.x === 0 && prev.y === 0) {
          return { x: -(UNIVERSE_W / 2 - width / 2), y: -(UNIVERSE_H / 2 - height / 2) }
        }
        return prev
      })
    })
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  // Keep minimapW in sync with viewport width so coordinate math is always correct
  useEffect(() => {
    const update = () => setMinimapW(window.innerWidth <= 599 ? 110 : MINIMAP_W)
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  const minimapH      = Math.round(minimapW * (UNIVERSE_H / UNIVERSE_W))
  const mmScaleActual = minimapW / UNIVERSE_W

  // Clamp offset so universe doesn't drift too far off-screen (scale-aware)
  const clamp = useCallback((x: number, y: number, vw: number, vh: number, s: number) => {
    const pad = 60
    return {
      x: Math.min(pad, Math.max(vw  - UNIVERSE_W * s - pad, x)),
      y: Math.min(pad, Math.max(vh  - UNIVERSE_H * s - pad, y)),
    }
  }, [])

  // Zoom centred on a point in container-local coords
  const zoomAt = useCallback((localX: number, localY: number, factor: number) => {
    const prevScale = scaleRef.current
    const newScale  = Math.max(MIN_SCALE, Math.min(MAX_SCALE, prevScale * factor))
    setScale(newScale)
    setOffset(prev => {
      const el = containerRef.current
      const vw = el?.clientWidth ?? 800; const vh = el?.clientHeight ?? 520
      const ux = (localX - prev.x) / prevScale   // universe coords of focal point
      const uy = (localY - prev.y) / prevScale
      return clamp(localX - ux * newScale, localY - uy * newScale, vw, vh, newScale)
    })
  }, [clamp])

  // Scroll-wheel zoom
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const rect = el.getBoundingClientRect()
      zoomAt(e.clientX - rect.left, e.clientY - rect.top, e.deltaY < 0 ? 1.1 : 1 / 1.1)
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [zoomAt])

  const stopMomentum = () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }

  const launchMomentum = useCallback(() => {
    const d = dragRef.current
    const tick = () => {
      d.vx *= 0.91; d.vy *= 0.91
      if (Math.abs(d.vx) < 0.25 && Math.abs(d.vy) < 0.25) return
      setOffset(prev => {
        const el = containerRef.current
        return clamp(prev.x + d.vx, prev.y + d.vy, el?.clientWidth ?? 800, el?.clientHeight ?? 520, scaleRef.current)
      })
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
  }, [clamp])

  // ── Drag handlers ──
  const startDrag = (cx: number, cy: number) => {
    stopMomentum()
    const d = dragRef.current
    d.active = true; d.moved = false
    d.sx = cx; d.sy = cy; d.ox = offset.x; d.oy = offset.y
    d.vx = 0; d.vy = 0; d.lx = cx; d.ly = cy; d.lt = Date.now()
    setHintVisible(false)
  }
  const moveDrag = (cx: number, cy: number) => {
    const d = dragRef.current
    if (!d.active) return
    const now = Date.now(); const dt = Math.max(1, now - d.lt)
    d.vx = (cx - d.lx) / dt * 14; d.vy = (cy - d.ly) / dt * 14
    d.lx = cx; d.ly = cy; d.lt = now
    if (Math.abs(cx - d.sx) > 4 || Math.abs(cy - d.sy) > 4) d.moved = true
    const el = containerRef.current
    setOffset(clamp(d.ox + cx - d.sx, d.oy + cy - d.sy, el?.clientWidth ?? 800, el?.clientHeight ?? 520, scaleRef.current))
  }
  const endDrag = () => { dragRef.current.active = false; launchMomentum() }

  // ── Unified touch handlers (single-finger drag + two-finger pinch) ──
  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault()
    const d = dragRef.current
    if (e.touches.length === 2) {
      stopMomentum()
      d.active = false; d.pinching = true
      const t1 = e.touches[0]; const t2 = e.touches[1]
      const el = containerRef.current
      const rect = el?.getBoundingClientRect()
      d.pinchDist0  = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY)
      d.pinchScale0 = scaleRef.current
      d.pinchMx = (t1.clientX + t2.clientX) / 2 - (rect?.left ?? 0)
      d.pinchMy = (t1.clientY + t2.clientY) / 2 - (rect?.top  ?? 0)
    } else if (e.touches.length === 1) {
      d.pinching = false
      startDrag(e.touches[0].clientX, e.touches[0].clientY)
    }
  }
  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault()
    const d = dragRef.current
    if (d.pinching && e.touches.length === 2) {
      const t1 = e.touches[0]; const t2 = e.touches[1]
      const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY)
      const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, d.pinchScale0 * (dist / d.pinchDist0)))
      const prevScale = scaleRef.current
      setScale(newScale)
      setOffset(prev => {
        const el = containerRef.current
        const vw = el?.clientWidth ?? 800; const vh = el?.clientHeight ?? 520
        const ux = (d.pinchMx - prev.x) / prevScale
        const uy = (d.pinchMy - prev.y) / prevScale
        return clamp(d.pinchMx - ux * newScale, d.pinchMy - uy * newScale, vw, vh, newScale)
      })
    } else if (!d.pinching && e.touches.length === 1) {
      moveDrag(e.touches[0].clientX, e.touches[0].clientY)
    }
  }
  const handleTouchEnd = (e: React.TouchEvent) => {
    const d = dragRef.current
    if (e.touches.length < 2) d.pinching = false
    if (e.touches.length === 0) endDrag()
  }

  const handlePlanetClick = (shop: EnrichedPublicShop, ev: React.MouseEvent) => {
    ev.stopPropagation()
    if (dragRef.current.moved) return
    setSelectedShop(s => s?.id === shop.id ? null : shop)
  }

  const handleMinimapClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    const rect = e.currentTarget.getBoundingClientRect()
    const ux = (e.clientX - rect.left) / mmScaleActual
    const uy = (e.clientY - rect.top)  / mmScaleActual
    const el = containerRef.current
    const vw = el?.clientWidth ?? 800; const vh = el?.clientHeight ?? 520
    const s  = scaleRef.current
    setOffset(clamp(vw / 2 - ux * s, vh / 2 - uy * s, vw, vh, s))
  }

  // Minimap viewport rect accounts for current zoom level and actual minimap size
  const vpLeft = (-offset.x / scale) * mmScaleActual
  const vpTop  = (-offset.y / scale) * mmScaleActual
  const vpW    = (viewSize.w / scale) * mmScaleActual
  const vpH    = (viewSize.h / scale) * mmScaleActual

  return (
    <div
      ref={containerRef}
      className="space-explorer"
      onMouseDown={e => startDrag(e.clientX, e.clientY)}
      onMouseMove={e => moveDrag(e.clientX, e.clientY)}
      onMouseUp={endDrag}
      onMouseLeave={endDrag}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onClick={() => setSelectedShop(null)}
      style={{ touchAction: 'none' }}
    >
      {/* Universe canvas — panned + scaled */}
      <div
        className="space-universe"
        style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`, transformOrigin: '0 0' }}
      >
        {/* Universe boundary — visible edge so users know space ends here */}
        <div className="space-universe-boundary" style={{ width: UNIVERSE_W, height: UNIVERSE_H }} />

        {/* Stars */}
        {STARS.map((s, i) => (
          <div key={i} className="space-star" style={{
            left: s.x, top: s.y,
            width: s.r * 2, height: s.r * 2,
            opacity: s.op,
            animationDelay: `${s.tw}s`,
          }} />
        ))}

        {/* Sun — website logo at center of universe */}
        <div
          className="space-sun"
          style={{ left: SUN_X - SUN_RADIUS, top: SUN_Y - SUN_RADIUS, width: SUN_RADIUS * 2, height: SUN_RADIUS * 2 }}
        >
          <div className="space-sun-corona space-sun-corona--1" />
          <div className="space-sun-corona space-sun-corona--2" />
          <div className="space-sun-corona space-sun-corona--3" />
          <img src="/logo.png" alt="Dukkan" className="space-sun-logo" draggable={false} />
        </div>

        {/* Category clusters */}
        {layout.clusters.map(cluster => (
          <span key={cluster.category}>
            {/* Nebula glow */}
            <div
              className="space-cluster-nebula"
              style={{
                left: cluster.cx - cluster.nebulaR,
                top:  cluster.cy - cluster.nebulaR,
                width:  cluster.nebulaR * 2,
                height: cluster.nebulaR * 2,
                background: `radial-gradient(circle, rgba(${cluster.rgb},0.13) 0%, rgba(${cluster.rgb},0.04) 55%, transparent 75%)`,
                boxShadow: `0 0 60px 10px rgba(${cluster.rgb},0.08)`,
              }}
            />
            {/* Cluster boundary ring */}
            <div
              className="space-cluster-ring"
              style={{
                left: cluster.cx - cluster.ringR,
                top:  cluster.cy - cluster.ringR,
                width:  cluster.ringR * 2,
                height: cluster.ringR * 2,
                borderColor: `rgba(${cluster.rgb},0.55)`,
                boxShadow: `0 0 18px 2px rgba(${cluster.rgb},0.18), inset 0 0 18px 2px rgba(${cluster.rgb},0.06)`,
              }}
            />
            {/* Category label badge */}
            <div
              className="space-cluster-label"
              style={{
                left: cluster.cx,
                top: cluster.cy - cluster.ringR - 16,
                color: cluster.color,
                borderColor: `rgba(${cluster.rgb},0.5)`,
                background: `rgba(${cluster.rgb},0.12)`,
              }}
            >
              {cluster.category}
            </div>
            {/* Planets in cluster */}
            {cluster.planets.map(planet => (
              <SpaceWorldPlanet
                key={shops[planet.shopIndex].id}
                shop={shops[planet.shopIndex]}
                x={planet.x}
                y={planet.y}
                size={planet.size}
                selected={selectedShop?.id === shops[planet.shopIndex].id}
                clusterColor={cluster.color}
                clusterRgb={cluster.rgb}
                onClick={(e) => handlePlanetClick(shops[planet.shopIndex], e)}
              />
            ))}
          </span>
        ))}

        {/* Uncategorized — free-floating in outer space */}
        {layout.freePlanets.map(planet => (
          <SpaceWorldPlanet
            key={shops[planet.shopIndex].id}
            shop={shops[planet.shopIndex]}
            x={planet.x}
            y={planet.y}
            size={planet.size}
            selected={selectedShop?.id === shops[planet.shopIndex].id}
            onClick={(e) => handlePlanetClick(shops[planet.shopIndex], e)}
          />
        ))}
      </div>

      {/* Scan detail panel */}
      {selectedShop && (
        <ScanPanel
          shop={selectedShop}
          onEnter={() => onShopClick(selectedShop)}
          onShare={e => onShare(e, selectedShop)}
          onClose={() => setSelectedShop(null)}
        />
      )}

      {/* Minimap */}
      <div
        className="space-minimap"
        style={{ width: minimapW, height: minimapH }}
        onClick={handleMinimapClick}
        title="Click to teleport"
      >
        <span className="space-minimap-title">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
          MAP
        </span>
        {/* Sun dot in minimap — always centered since it's at universe center */}
        <div className="space-minimap-sun" style={{ left: '50%', top: '50%' }} />
        {/* Cluster planet dots */}
        {layout.clusters.map(cluster =>
          cluster.planets.map(planet => (
            <div
              key={shops[planet.shopIndex].id}
              className={`space-minimap-dot${selectedShop?.id === shops[planet.shopIndex].id ? ' space-minimap-dot--active' : ''}`}
              style={{ left: planet.x * mmScaleActual, top: planet.y * mmScaleActual, background: cluster.color }}
              title={shops[planet.shopIndex]?.name}
            />
          ))
        )}
        {/* Free planet dots */}
        {layout.freePlanets.map(planet => (
          <div
            key={shops[planet.shopIndex].id}
            className={`space-minimap-dot${selectedShop?.id === shops[planet.shopIndex].id ? ' space-minimap-dot--active' : ''}`}
            style={{ left: planet.x * mmScaleActual, top: planet.y * mmScaleActual }}
            title={shops[planet.shopIndex]?.name}
          />
        ))}
        <div
          className="space-minimap-viewport"
          style={{ left: vpLeft, top: vpTop, width: Math.min(vpW, minimapW), height: Math.min(vpH, minimapH) }}
        />
      </div>


      {/* Floating overlay toolbar — Explore/Discover toggle + Filters */}
      <div className="space-overlay-toolbar" onClick={e => e.stopPropagation()}>
        <button className="space-mode-toggle space-mode-toggle--space" onClick={onExploreMode} title="Back to Explore">
          <span className="space-mode-toggle-option">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
              <rect x="1" y="5" width="5" height="14" rx="2" opacity="0.55"/>
              <rect x="9" y="3" width="6" height="18" rx="2.5"/>
              <rect x="18" y="5" width="5" height="14" rx="2" opacity="0.55"/>
            </svg>
            Explore
          </span>
          <span className="space-mode-toggle-option active">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="12" r="3"/><circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2"/>
              <circle cx="19" cy="5" r="2"/><circle cx="5" cy="18" r="1.5"/>
            </svg>
            Discover
          </span>
          <span className="space-mode-toggle-pill" />
        </button>
        <button
          className={`shop-browser-filter-btn${activeFilterCount > 0 ? ' has-filter' : ''}`}
          onClick={onFilterOpen}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 4a1 1 0 0 1 1-1h16a1 1 0 0 1 .8 1.6l-6 8v5.4a1 1 0 0 1-.55.9l-4 2A1 1 0 0 1 9 20v-7.4L3.2 4.6A1 1 0 0 1 3 4z"/>
          </svg>
          Filters
          {activeFilterCount > 0 && <span className="shop-browser-filter-badge">{activeFilterCount}</span>}
        </button>
      </div>

      {/* Drag hint */}
      {hintVisible && (
        <div className="space-drag-hint">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 11V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0"/><path d="M14 10V4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v2"/><path d="M10 10.5V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v8"/>
            <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15"/>
          </svg>
          Drag to explore the universe
        </div>
      )}

      {/* Worlds count */}
      <div className="space-count-badge">{shops.length} {shops.length === 1 ? 'world' : 'worlds'}</div>
    </div>
  )
}

// ── Individual planet in the free-roam universe ──
function SpaceWorldPlanet({ shop, x, y, size, selected, onClick, clusterColor, clusterRgb }: {
  shop: EnrichedPublicShop; x: number; y: number; size: number; selected: boolean
  onClick: (e: React.MouseEvent) => void
  clusterColor?: string; clusterRgb?: string
}) {
  const posterSrc = posterUrl(shop.id)
  const [imgError, setImgError] = useState(false)
  const color  = clusterColor ?? '#a78bfa'
  const rgb    = clusterRgb  ?? '167,139,250'
  return (
    <button
      className={`space-world-planet${selected ? ' space-world-planet--selected' : ''}${shop.has3DViewer ? ' space-world-planet--live' : ''}`}
      style={{
        left: x - size / 2, top: y - size / 2, width: size, height: size,
        '--planet-color': color, '--planet-rgb': rgb,
      } as React.CSSProperties}
      onClick={onClick}
      aria-label={shop.name}
    >
      {!imgError
        ? <img src={posterSrc} alt={shop.name} onError={() => setImgError(true)} draggable={false} />
        : <span className="space-planet-placeholder">🏪</span>
      }
      <div className="space-planet-shine" />
      {selected && <div className="space-planet-selected-ring" />}
      {shop.has3DViewer && !selected && <div className="space-planet-live-ring" />}
      <span className="space-world-label">{shop.name}</span>
    </button>
  )
}

